<template>

    <div class="max-width-md-large m-auto">
        <div class="card card--regular card--green mb-3">
            <div class="text-center">
                <h3 class="mb-3 sm-mb-2 text-center">
                    Summary and payment
                </h3>
                <div class="fw-light fs-20 sm-fs-18 mb-3 sm-mb-2">
                    You are agreeing to make a <span class="lowercase fw-bold">{{ type.label }}</span> of
                    <span class="fw-bold">{{ currencyFormat(form.donation_amount) }}</span>
                    in support of <span class="fw-bold">{{ title }}</span>.
                </div>
            </div>

            <div v-show="type.id===1">
                <form id="payment-form">
                    <label for="cardnumber" class="form-group-label required">
                        Card Details
                    </label>
                    <div ref="card"></div>
                    <span
                        v-if="card_error"
                        class="txt-error mt-1">
                        {{ card_error }}
                    </span>
                </form>
            </div>

            <div v-show="type.id===2">

                <div class="mb-3 sm-mb-2 mx--8px sm-mx--0">
                    <div class="mx-1 sm-mx-0">
                        <label for="day_of_month" class="form-group-label c-charcoal required">Preferred collection day</label>
                        <select
                            name="day_of_month"
                            id="day_of_month"
                            class="input-select full-width"
                            v-model="form.day_of_month">
                            <option
                                v-for="day in days_in_month"
                                :key="day"
                                :value="day">{{ day }}</option>
                        </select>
                    </div>
                </div>

            </div>

        </div>

        <div class="card card--large card--green mb-3">
            <div class="d-flex justify-content-center mb-4 sm-mb-2">
                <img :src="giftAidLogo" alt="Gift Aid Logo">
            </div>

            <p class="text-center mb-3 sm-mb-2">
                If you are a UK taxpayer you can boost your donation by
                <span class="fw-bold">{{ currencyFormat(giftAid) }}</span>
                <span v-if="type.id===2" class="fw-bold">per month</span>
                at no extra cost to you.</p>

            <div class="mb-3 sm-mb-2 d-flex justify-content-center">
                <label
                    class="checkbox"
                    :class="{'disabled' : gift_aid.disabled}">
                    <input type="checkbox"
                           class="input-checkbox display-none"
                           :disabled="gift_aid.disabled"
                           v-model="gift_aid.value">
                    <span class="checkmark"></span>{{ gift_aid.label }}
                </label>
            </div>

            <div class="fs-14 lh-13">
                <div class="fw-light mb-3 sm-mb-2">
                    I understand I must pay enough income tax and/or capital gains tax each tax year to cover the amount of Gift Aid that all charities and community amateur sports clubs claim on my donations in that tax year, and I am responsible for paying any difference.
                </div>
                <div class="fw-light">
                    If your tax status, name or address change, or if you wish to cancel your Gift Aid declaration please let us know by emailing <a href="mailto:fundraising@minsteadtrust.org.uk">fundraising@minsteadtrust.org.uk</a>
                </div>
            </div>

        </div>

        <div class="card card--regular card--green mb-3">
            <div class="d-flex flex-wrap align-items-center">
                <div class="fw-bold fs-20">
                    Add
                    {{ currencyFormat(processingFee) }}
                </div>
                <div class="pl-2">
                    <label
                        class="checkbox"
                        :class="{'disabled' : processing_fee.disabled}">
                        <input type="checkbox"
                               class="input-checkbox"
                               :disabled="processing_fee.disabled"
                               v-model="processing_fee.value">
                        <span class="checkmark"></span>
                        {{ processing_fee.label }}
                    </label>
                </div>
            </div>
        </div>


        <div class="d-flex align-items-center justify-content-center flex-wrap">
            <ClipSpinner
                v-if="formBusy"
                class="mr-1"
                :loading="true"
                size="30px"
                :with-wrapper="false"/>

                <div class="d-flex flex-wrap align-items-center w-100 mb-3 justify-content-center">
                    <vue-recaptcha @verify="onVerify" sitekey="6Lek2-AZAAAAABCdCFcBaeG_Qp17mYeIfbaB-mVI" :loadRecaptchaScript="true">
                    </vue-recaptcha>
                    <label v-if="this.form.robot" class='w-100 text-center pt-2 txt-error' style="cursor: initial;" >Please submit recaptcha</label>
                </div>
                <button
                    class="btn --default"
                    :disabled="formBusy"
                    @click="submit">
                    Make your donation of {{ currencyFormat(form.amount) }}
                </button>
        </div>

        <span v-if="type.id === 2 && redirect_flow_error" class="d-flex txt-error mt-3 justify-content-center">{{ redirect_flow_error }}</span>

    </div>

</template>

<script>

    import axios from 'axios';
    import {loadStripe} from '@stripe/stripe-js';
    import {mapGetters,mapActions} from "vuex";

    import ClipSpinner from "@/components/spinners/ClipSpinner";
    import Formatter from '@/mixins/formatters';
    import Calculations from '@/mixins/calculations';
    import { DaysInMonth } from '@/configuration/DaysInMonth';
    import Cookies from 'js-cookie';
    import VueRecaptcha from "vue-recaptcha";

    export default {
        name: 'DonationPayment',
        mixins: [Formatter, Calculations],
        components: {ClipSpinner, VueRecaptcha},
        props: {
            stripeKey: {
                type: String,
                required: true
            },
            title: {
                type: String,
                required: true
            },
            amount: {
                type: Number,
                required: true
            },
            amountDescription: {
                type: String,
                required: true
            },
            type: {
                type: Object,
                required: true
            },
            postMeta: {
                type: Object,
                required: true
            },
            monthlyDonationRedirectToCustomPage: {
                type: String,
                required: false,
                default: false
            }
        },

        data() {
            return {

                days_in_month: DaysInMonth,

                processing_fee: {
                    label: 'Help cover Minstead Trust’s processing fees',
                    value: false,
                    disabled: false
                },
                gift_aid: {
                    label: 'Yes - I am a UK taxpayer and would like to Gift Aid my donation. I also agree for Gift Aid to be claimed on any future donations I make to Minstead Trust and any donations I have made to the Trust in the last four years.',
                    value: false,
                    disabled: false
                },
                form: {
                    donation_type: this.type.id,
                    amount: this.amount, // total amount -> donation_amount + processing_fee
                    day_of_month: 1,
                    donation_amount: this.amount,
                    donation_amount_description: this.amountDescription,
                    gift_aid_amount: 0,
                    processing_fee: 0,
                    robot: false,
                    captcha: ''
                },

                creating_payment_intent: false,
                making_payment: false,

                creating_redirect_flow: false,
                redirect_flow_error: null,

                stripe: null,
                elements: null,
                card: null,

                card_error: null,

                style: {
                    base: {
                        color: '#3C3C3B',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", gotham, arial, sans-serif',
                        fontSmoothing: 'antialiased',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#6F6F6F'
                        },
                    },
                    invalid: {
                        color: '#DD391C',
                    }
                }

            }
        },

        computed: {
            ...mapGetters('Donations', ['donationSettings','donationDetails']),
            base() {
                return window.imgUrl;
            },
            giftAidLogo() {
                return this.base + '/svg/gift-aid-logo.svg';
            },
            giftAidSettings() {
                return this.donationSettings.gift_aid;
            },
            goCardlessSettings() {
                return this.donationSettings.go_cardless;
            },
            stripeSettings() {
                return this.donationSettings.stripe;
            },
            goCardlessProcessingFee() {
                const fee = this.goCardlessSettings.processing_fee_percentage;
                return fee
                    ? this.percentOf(this.form.donation_amount, fee)
                    : 0;
            },
            stripeProcessingFee() {
                const fee = this.stripeSettings.processing_fee_percentage;
                return fee
                    ? this.percentOf(this.form.donation_amount, fee)
                    : 0;
            },
            processingFee() {
               return this.type.id === 1
                ? this.stripeProcessingFee
                : this.goCardlessProcessingFee;
            },
            processingFeeAmount() {
                return this.processing_fee.value
                    ? this.processingFee
                    : 0;
            },
            giftAid() {
                const giftAidPercentage = this.giftAidSettings.percentage;
                return giftAidPercentage
                    ? this.percentOf(this.form.donation_amount, giftAidPercentage)
                    : 0;
            },
            giftAidAmount() {
                return this.gift_aid.value
                    ? this.giftAid
                    : 0;
            },
            totalAmount() {
                return this.form.donation_amount + this.form.processing_fee;
            },

            formBusy() {
                return !! (
                    this.creating_payment_intent ||
                    this.making_payment ||
                    this.creating_redirect_flow
                );
            },

            billingDetails() {
                return {
                    address: {
                        city: this.donationDetails.town_or_city,
                        country: this.donationDetails.country,
                        line1: this.donationDetails.address_line_one,
                        line2: this.donationDetails.address_line_two,
                        postal_code: this.donationDetails.post_code,
                        state: null
                    },
                    email: this.donationDetails.email,
                    name: this.donationDetails.name,
                };
            }
        },

        watch: {
            giftAidAmount: {
                immediate: true,
                handler(val) {
                    this.form.gift_aid_amount = val;
                },
            },
            processingFeeAmount: {
                immediate: true,
                handler(val) {
                    this.form.processing_fee = val;
                },
            },
            totalAmount: {
                immediate: true,
                handler(val) {
                    this.form.amount = val;
                },
            },
        },

        mounted: function () {
            this.loadStripe();
        },

        methods: {
            ...mapActions('Donations', ['changeStep']),

            async loadStripe() {
                this.stripe = await loadStripe(this.stripeKey);

                this.elements = this.stripe.elements();
                this.card = this.elements.create('card', {style: this.style});
                this.card.mount(this.$refs.card);
            },

            submit() {

                if(this.form.captcha) {
                    if (this.type.id === 1) {
                        this.submitStripe();
                    } else {
                        this.submitGoCardless();
                    }
                } else {
                    this.form.robot = true;
                }
            },

            submitGoCardless() {
                if (this.formBusy) return;
                this.redirect_flow_error = null;
                this.creating_redirect_flow = true;
                return new Promise((resolve, reject) => {
                    axios.post('/api/donations/gocardless/redirect-flow', {
                        'description': this.title,
                        'first_name': this.donationDetails.first_name,
                        'last_name': this.donationDetails.last_name,
                        'email': this.donationDetails.email,
                        'day_of_month': this.form.day_of_month.toString(),
                        'interval_unit': 'monthly',
                        'amount': this.form.amount.toString(),
                        'captcha': this.form.captcha,
                        'success_redirect_url' : this.monthlyDonationRedirectToCustomPage
                    }).then(response => {

                        let sessionToken = response.data.session_token;
                        // The redirect flow session token is required to
                        // validate the request is from the same person
                        // when completing the request
                        Cookies.set('gocardless_session_token', sessionToken);
                        // The same token is stored as the key and contains
                        // all meta data, this is because GoCardless only
                        // supports the storing of a maximum of 3 keys
                        Cookies.set(sessionToken, this.prepareFormData().meta);

                        // We now redirect the user to the GoCardless
                        // process and they will return to the
                        // website on successfull completion
                        window.location = response.data.redirect_url;
                        this.creating_payment_intent = false;
                        return resolve(response);
                    }).catch((error) => {
                        this.creating_redirect_flow = false;
                        this.redirect_flow_error = error.response.data.message;
                        return reject();
                    });
                });
            },

            submitStripe() {

                if (this.formBusy) return;

                this.clearCardErrors();

                this.creating_payment_intent = true;

                let form = this.prepareFormData();

                this.createPaymentIntent(form).then(({data}) => {

                    let formData = this.prepareFormData();
                    formData['meta']['type_label'] = this.type.label;
                    Cookies.set('donation_details', formData);

                    this.making_payment = true;
                    this.creating_payment_intent = false;
                    this.attemptPayment(data);
                });
                // this.$emit('payment-success', this.form);

            },

            clearCardErrors() {
                this.card_error = null;
            },

            prepareFormData() {
                return {
                    amount: this.form.amount,
                    meta: {...this.form, ...this.postMeta, ...this.donationDetails}
                }
            },

            createPaymentIntent(form) {
                return new Promise((resolve, reject) => {
                    axios.post('/api/donations/stripe/payment-intent', form)
                        .then(response => {
                            return resolve(response);
                        })
                        .catch((error) => {
                            this.creating_payment_intent = false;
                            return reject();
                        });
                });
            },

            attemptPayment(data) {
                let secret = data['client_secret'];
                const $this = this;
                this.stripe.confirmCardPayment(secret, {
                        payment_method: {
                            card: $this.card,
                            billing_details: $this.billingDetails,
                        },
                    })
                    .then(function(result) {

                        if (result.error) {
                            $this.card_error = result.error.message;
                        } else {
                            $this.$emit('payment-success', $this.form)
                        }

                        $this.making_payment = false;
                    });
            },

            onVerify: function (response) {
                if (response) this.form.captcha = response;
            },
        }
    }

</script>
